////////////////////////////////////////
// # GOLDEN RULE!!!! HONOR ABOVE ALL!!!!
////////////////////////////////////////
// NO NEW FILES!!!!!!!!!
////////////////////////////////////////

// I. MANDATORY FILE HEADER
// ⚪ [Subsection 1: PURE WHITE] BitsBound Pricing Pea - Presentation layer for pricing page
// ⚪ [Subsection 2: PURE WHITE] Created: 2026-01-02
// ⚪ [Subsection 3: PURE WHITE] Purpose: Pure presentation component rendering pricing UI
// ⚪ [Subsection 4: PURE WHITE] Version: 1.0.0 - World-Class Pricing Implementation
// ⚪ [Subsection 5: PURE WHITE] Copyright: © 2026 BitsBound (TayTrom Holdings) - All Rights Reserved

// II. ASCII FUNCTION FLOW DIAGRAM
/*
╔════════════════════════════════════════════════════════════════════════════════════════════╗
║                                 PRICING PEA PRESENTATION                                    ║
║                                                                                             ║
║    Props → Render → Styled Components → User Sees Beautiful UI                             ║
║       ↓       ↓           ↓                    ↓                                           ║
║    [Data]  [JSX]     [CSS Modules]        [Browser]                                        ║
║                                                                                             ║
║    SECTIONS RENDERED:                                                                       ║
║    ├─ Header with logo and navigation                                                      ║
║    ├─ Hero section with title and free trial badge                                         ║
║    ├─ Monthly/Annual billing toggle                                                        ║
║    ├─ Pricing tier cards grid (4 tiers)                                                    ║
║    ├─ Credit system explainer                                                              ║
║    ├─ FAQ accordion                                                                        ║
║    ├─ Footer CTA                                                                           ║
║    └─ Footer with links                                                                    ║
╚════════════════════════════════════════════════════════════════════════════════════════════╝
*/

// III. IMPORTS
import React from 'react';
import styles from './Bitsbound_Kings_Home_Primary_SRC_Pricing_Lobby_Pea.module.css';
import type { PricingTier, PricingFaq } from '../pod/Bitsbound_Kings_Home_Primary_SRC_Pricing_Lobby_Pod.js';

// IV. INDEPENDENT CONSTANTS
// 🟦 [Subsection 10: BRIGHT TEAL] TypeScript interface for component props
interface PricingPeaProps {
    tiers: PricingTier[];
    faqs: PricingFaq[];
    expandedFaqIndex: number | null;
    onFaqToggle: (index: number) => void;
    onCtaClick: (tier: PricingTier) => void;
    onBackToHome: () => void;
    authBaseUrl: string;
}

// V. FUNCTION/CLASS NAME
/**
 * PricingPea - Presentation layer for the pricing page
 *
 * Pure presentation component that renders all pricing UI elements.
 * Receives all data and handlers via props from PricingPod.
 */
const PricingPea: React.FC<PricingPeaProps> = ({
    tiers,
    faqs,
    expandedFaqIndex,
    onFaqToggle,
    onCtaClick,
    onBackToHome,
    authBaseUrl,
}) => {
    // VI. DECLARATIONS
    // No local state - this is a pure presentation component

    // VII. RENDER HELPERS
    // 🔴 [Subsection 20: RED] Format price for display
    const formatPrice = (tier: PricingTier): string => {
        return tier.monthlyPrice.toLocaleString();
    };

    // VIII. RENDER
    return (
        <div className={styles.pricingPage}>
            {/* ============================================ */}
            {/* HEADER                                       */}
            {/* ============================================ */}
            <header className={styles.header}>
                <div className={styles.headerContent}>
                    <div className={styles.logoSection} onClick={onBackToHome}>
                        <img
                            src="https://www.bitsbound.com/favicon.svg"
                            alt="BitsBound"
                            className={styles.logoImage}
                        />
                        <span className={styles.logoText}>BitsBound</span>
                    </div>
                    <div className={styles.headerCta}>
                        <a href="/" className={styles.headerLink}>Home</a>
                        <a href="/documentation" className={styles.headerLink}>Docs</a>
                        <a href={`${authBaseUrl}/auth/sign-in`} className={styles.headerLink}>Sign In</a>
                        <a href={`${authBaseUrl}/auth/register`} className={styles.headerButton}>
                            Start Free Trial
                        </a>
                    </div>
                </div>
            </header>

            {/* ============================================ */}
            {/* MAIN CONTENT                                 */}
            {/* ============================================ */}
            <main className={styles.mainContent}>
                {/* ============================================ */}
                {/* HERO SECTION                                 */}
                {/* ============================================ */}
                <section className={styles.heroSection}>
                    <h1 className={styles.heroTitle}>
                        Simple, Transparent Pricing
                    </h1>
                    <p className={styles.heroSubtitle}>
                        Partner-level contract analysis powered by AI. One flat rate of $30 per million credits across all tiers.
                        No hidden fees. No per-seat charges. Just powerful contract automation.
                    </p>
                    <div className={styles.freeTrial}>
                        <span className={styles.freeTrialIcon}>🎁</span>
                        <span>1.5M free credits (~$45 value) on signup — no credit card required</span>
                    </div>
                </section>

                {/* ============================================ */}
                {/* ALL TIERS INCLUDE                            */}
                {/* ============================================ */}
                <div className={styles.allTiersInclude}>
                    <h3 className={styles.allTiersTitle}>All Tiers Include</h3>
                    <div className={styles.allTiersFeatures}>
                        <span className={styles.allTiersFeature}>All 9 contract verticals</span>
                        <span className={styles.allTiersDivider}>•</span>
                        <span className={styles.allTiersFeature}>Real OOXML Track Changes</span>
                        <span className={styles.allTiersDivider}>•</span>
                        <span className={styles.allTiersFeature}>Negotiation email drafts</span>
                        <span className={styles.allTiersDivider}>•</span>
                        <span className={styles.allTiersFeature}>Risk analysis dashboard</span>
                        <span className={styles.allTiersDivider}>•</span>
                        <span className={styles.allTiersFeature}>Playbook customization</span>
                        <span className={styles.allTiersDivider}>•</span>
                        <span className={styles.allTiersFeature}>Email support</span>
                    </div>
                </div>

                {/* ============================================ */}
                {/* PRICING CARDS                                */}
                {/* ============================================ */}
                <div className={styles.pricingGrid}>
                    {tiers.map((tier, index) => (
                        <div
                            key={tier.id}
                            className={`${styles.pricingCard} ${tier.highlighted ? styles.highlighted : ''} ${styles.fadeInUp} ${styles[`animationDelay${index + 1}`]}`}
                        >
                            {tier.badge && (
                                <div className={styles.cardBadge}>{tier.badge}</div>
                            )}

                            <div className={styles.cardHeader}>
                                <h2 className={styles.tierName}>{tier.name}</h2>
                                <p className={styles.tierDescription}>{tier.description}</p>
                            </div>

                            <div className={styles.priceDisplay}>
                                <div className={styles.priceAmount}>
                                    <span className={styles.currencySymbol}>$</span>
                                    <span className={styles.priceValue}>{formatPrice(tier)}</span>
                                </div>
                                <div className={styles.pricePeriod}>/month</div>
                                <div className={styles.creditsInfo}>
                                    <span className={styles.creditsAmount}>{tier.creditsFormatted}</span>
                                    <span>credits included</span>
                                </div>
                            </div>

                            {tier.id === 'tier_four' && (
                                <div className={styles.tierPerk}>
                                    <span className={styles.tierPerkIcon}>⭐</span>
                                    <span>Dedicated account manager</span>
                                </div>
                            )}

                            <button
                                className={`${styles.ctaButton} ${tier.highlighted ? styles.primary : styles.secondary}`}
                                onClick={() => onCtaClick(tier)}
                            >
                                {tier.ctaText}
                            </button>
                        </div>
                    ))}
                </div>

                {/* ============================================ */}
                {/* CREDIT EXPLAINER                             */}
                {/* ============================================ */}
                <section className={styles.creditExplainer}>
                    <div className={styles.creditExplainerContent}>
                        <h2 className={styles.sectionTitle}>How Credits Work</h2>
                        <p className={styles.sectionDescription}>
                            BitsBound uses a transparent credit-based system. Credits represent AI processing capacity
                            consumed during contract analysis. <span className={styles.highlightTeal}>$30 per million credits</span> —
                            the same rate across all tiers.
                        </p>
                        <div className={styles.creditCards}>
                            <div className={styles.creditCard}>
                                <div className={styles.creditCardIcon}>📄</div>
                                <h3 className={styles.creditCardTitle}>Usage Varies</h3>
                                <p className={styles.creditCardDescription}>
                                    Credit consumption ranges from <span className={styles.highlightTeal}>&lt;1M to ~30M</span> based on complexity.
                                    Simple NDA: &lt;1M. Complex 70-page redline: up to 30M. <span className={styles.highlightTeal}>First contract always completes free.</span>
                                </p>
                            </div>
                            <div className={styles.creditCard}>
                                <div className={styles.creditCardIcon}>⚡</div>
                                <h3 className={styles.creditCardTitle}>Token-Based</h3>
                                <p className={styles.creditCardDescription}>
                                    <span className={styles.highlightTeal}>1 credit = 1 token</span>. Both input tokens (contract text) and output tokens
                                    (AI analysis) are billed at the same rate for transparent pricing.
                                </p>
                            </div>
                            <div className={styles.creditCard}>
                                <div className={styles.creditCardIcon}>📊</div>
                                <h3 className={styles.creditCardTitle}>Transparent Tracking</h3>
                                <p className={styles.creditCardDescription}>
                                    View real-time credit usage in your dashboard. See exactly how many credits each analysis consumed
                                    and how many you have remaining.
                                </p>
                            </div>
                        </div>

                        {/* FAQ Accordion integrated into Credits section */}
                        <div className={styles.faqList}>
                            {faqs.map((faq, index) => (
                                <div
                                    key={index}
                                    className={`${styles.faqItem} ${expandedFaqIndex === index ? styles.expanded : ''}`}
                                >
                                    <div
                                        className={styles.faqQuestion}
                                        onClick={() => onFaqToggle(index)}
                                        role="button"
                                        tabIndex={0}
                                        onKeyDown={(e) => e.key === 'Enter' && onFaqToggle(index)}
                                        aria-expanded={expandedFaqIndex === index}
                                    >
                                        <span>{faq.question}</span>
                                        <span className={styles.faqIcon}>▼</span>
                                    </div>
                                    {expandedFaqIndex === index && (
                                        <div className={styles.faqAnswer}>
                                            {faq.answer}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* ============================================ */}
                {/* FOOTER CTA                                   */}
                {/* ============================================ */}
                <section className={styles.footerCta}>
                    <div className={styles.footerCtaContent}>
                        <h2 className={styles.footerCtaTitle}>Ready to Transform Your Contract Workflow?</h2>
                        <p className={styles.footerCtaSubtitle}>
                            Join attorneys who are using AI to accelerate contract analysis from days to minutes.
                            Start with 1.5M free credits — no credit card required.
                        </p>
                        <a href={`${authBaseUrl}/auth/register`} className={styles.footerCtaButton}>
                            <span>Start Free Trial</span>
                            <span>→</span>
                        </a>
                        <p className={styles.footerNote}>
                            No credit card required • Cancel anytime • All 9 contract verticals included
                        </p>
                    </div>
                </section>
            </main>

            {/* ============================================ */}
            {/* FOOTER                                       */}
            {/* ============================================ */}
            <footer className={styles.footer}>
                <div className={styles.footerLinks}>
                    <a href="/" className={styles.footerLink}>Home</a>
                    <a href="/about" className={styles.footerLink}>About</a>
                    <a href="/documentation" className={styles.footerLink}>Documentation</a>
                    <a href="/privacy" className={styles.footerLink}>Privacy</a>
                    <a href="/terms" className={styles.footerLink}>Terms</a>
                    <a href="/security" className={styles.footerLink}>Security</a>
                    <a href="/dpa" className={styles.footerLink}>DPA</a>
                </div>
                <p className={styles.footerCopyright}>
                    © {new Date().getFullYear()} BitsBound. All rights reserved.
                </p>
            </footer>
        </div>
    );
};

export default PricingPea;